import React, { Fragment, useMemo } from "react";
import Text from "../../../../../shared-components/css-modules/lexus/atoms/Text";
import Collapse from "../../../../../shared-components/css-modules/lexus/molecules/Collapse";
import { formatStandardEquipmentList, getEcoSpecs } from "../../../../../../../common-deprecated/utils";
import { useCarConfigSelector } from "../../../../redux/store";
import styles from "./Summary.module.scss";
import { trackOrderOverviewSpecsPanelOpen } from "../../../../utils/tracking";
import { hasAlternativeEcoSpecFlow } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { calculateUnitPosition } from "../../../../../shared-logic/utils/rtlUitils";
import { SummaryEcoSpecs } from "./SummaryEcoSpecs";
import { SummaryEcoConsumption } from "./SummaryEcoConsumption";
import { CarConfigLabel, useCarConfigLabel } from "../../../../utils/labelUtils";
import Popover from "../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import CarDataConsolidator from "../../../../../../shared-logic/utils/classes/CarDataConsolidator";

const SummarySpecifications: React.FC = () => {
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const techSpecs = useCarConfigSelector((state) => state.specs.techSpecs);
    const standardEquipment = useCarConfigSelector(({ basket, specs, items }) => {
        const unformattedStandardEquipment = CarDataConsolidator.filterStandardEquipmentSpecs(
            basket,
            items,
            specs.standardEquipment,
        ).filter((extra) => CarDataConsolidator.isAvailable(extra, basket.colour, basket.upholstery).available);
        return formatStandardEquipmentList(unformattedStandardEquipment);
    });

    const alternativeEcoSpecFlow = hasAlternativeEcoSpecFlow(commonSettings);

    const ecoSpecs = useMemo(() => {
        return getEcoSpecs(techSpecs, true, alternativeEcoSpecFlow);
    }, [techSpecs]);

    const filteredTechSpecs = techSpecs.filter(
        (spec) => !ecoSpecs?.map((ecoSpec) => ecoSpec.internalCode).includes(spec.internalCode),
    );

    const techSpecsLabel = useCarConfigLabel("carConfigTechSpecs");
    return (
        <>
            <Text tag="h2" variant="apheleia-list-item" spacing="spacing-3" className={styles.title}>
                <CarConfigLabel label="carConfigSpecifications" />
            </Text>
            {filteredTechSpecs.length !== 0 && (
                <Collapse
                    summary={
                        <Text tag="h3" variant="apheleia-list-item-small">
                            {techSpecsLabel}
                        </Text>
                    }
                    onChange={(isOpen) => {
                        trackOrderOverviewSpecsPanelOpen(!!isOpen, "tech-specs");
                    }}
                    className={styles.collapse}
                >
                    {filteredTechSpecs.map((specs) => (
                        <Fragment key={specs.id}>
                            <Text tag="h4" variant="apheleia-list-item" className={styles.subtitle}>
                                {specs.title}
                            </Text>
                            <ul role="list" className={styles["config-sublist"]}>
                                {!!specs.data &&
                                    specs.data.map((data) => (
                                        <li className={styles["config-subitem"]} key={data.id}>
                                            <div className={styles["config-subitem-title"]}>
                                                <Text tag="p" variant="apheleia-body-small">
                                                    {data.name}
                                                </Text>
                                                {data.description && (
                                                    <Popover placement="top">
                                                        <Popover.Trigger>
                                                            <Button variant="clean">
                                                                <Icon variant="info" sizing="md" aria-hidden="true" />
                                                                <span className={a11y["sr-only"]}>
                                                                    <CarConfigLabel label="carConfigA11yButtonMoreInfoAbout" />
                                                                </span>
                                                            </Button>
                                                        </Popover.Trigger>
                                                        <Popover.Content>
                                                            <div
                                                                dangerouslySetInnerHTML={{ __html: data.description }}
                                                            />
                                                        </Popover.Content>
                                                    </Popover>
                                                )}
                                            </div>
                                            <Text variant="apheleia-body" className={styles["config-subitem-price"]}>
                                                {calculateUnitPosition(
                                                    commonSettings.culture.name,
                                                    data.value,
                                                    data.unit,
                                                )}
                                            </Text>
                                        </li>
                                    ))}
                            </ul>
                        </Fragment>
                    ))}
                </Collapse>
            )}
            {standardEquipment.map((category) => (
                <Collapse
                    summary={
                        <Text tag="h3" variant="apheleia-list-item-small">
                            {category.title}
                        </Text>
                    }
                    key={category.id}
                    onChange={(isOpen) => {
                        trackOrderOverviewSpecsPanelOpen(
                            !!isOpen,
                            category.internalCode === "EXT" ? "exterior" : "interior",
                        );
                    }}
                    className={styles.collapse}
                >
                    {category.subCat
                        ?.filter(({ data }) => !!data)
                        .map((subCategory) => (
                            <Fragment key={subCategory.id}>
                                <Text tag="h4" variant="apheleia-list-item" className={styles.subtitle}>
                                    {subCategory.title}
                                </Text>
                                <ul role="list" className={styles["config-sublist"]}>
                                    {subCategory.data?.map((data) => (
                                        <li className={styles["config-subitem"]} key={data.id}>
                                            <div className={styles["config-subitem-title"]}>
                                                <Text tag="p" variant="apheleia-body-small">
                                                    {data.name}
                                                </Text>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Fragment>
                        ))}
                </Collapse>
            ))}
            <SummaryEcoConsumption alternateFlow={alternativeEcoSpecFlow} />
            <SummaryEcoSpecs ecoSpecs={ecoSpecs} alternateFlow={alternativeEcoSpecFlow} />
        </>
    );
};

export default SummarySpecifications;
